<!-- FT-216 REQ-08, REQ-10 п.3, REQ-15: «Применено за 7 дней» с кнопкой «Откатить». Один компонент на виджет
     главной и блок настроек. Виден при любом значении «включено» и без услуги в плане: откат гейта услуги не имеет
     (CTR-10) — поэтому здесь нет ни dynamicPricingInPlan, ни флага enabled. Откат — после подтверждения. -->
<template>
  <div data-testid="dp-applied">
    <div class="text-subtitle-2 mb-1">{{ $t("dynamic_pricing.applied_title") }}</div>
    <div v-if="!items.length" class="text-body-2 text--secondary" data-testid="dp-applied-empty">
      {{ $t("dynamic_pricing.applied_empty") }}
    </div>
    <v-list v-else dense class="py-0">
      <template v-for="(rec, i) in items">
        <v-divider v-if="i > 0" :key="`dp-applied-divider-${rec.id}`" />
        <v-list-item :key="rec.id" class="px-0" :data-testid="`dp-applied-${rec.id}`">
          <v-list-item-content>
            <v-list-item-title class="text-wrap">
              <span class="font-weight-medium">{{ groupLabel(rec) }}</span>
              <span class="ml-1">{{ proposal(rec) }}</span>
            </v-list-item-title>
            <v-list-item-subtitle class="text-wrap">
              {{ appliedText(rec) }}
              <v-chip x-small label class="ml-1" data-testid="dp-applied-kind">{{ kindText(rec) }}</v-chip>
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action v-if="canRollback(rec)" class="my-0">
            <v-btn x-small text color="primary" :disabled="!!busyId" :loading="busyId === rec.id"
              data-testid="dp-rollback" @click="ask(rec)">{{ $t("dynamic_pricing.rollback") }}</v-btn>
          </v-list-item-action>
        </v-list-item>
      </template>
    </v-list>

    <v-dialog v-model="dialog" max-width="440" :persistent="!!busyId">
      <v-card data-testid="dp-rollback-dialog">
        <v-card-title class="text-h6">{{ $t("dynamic_pricing.rollback_title") }}</v-card-title>
        <v-card-text>
          <div v-if="target" class="font-weight-medium mb-2">{{ groupLabel(target) }} {{ proposal(target) }}</div>
          <div>{{ $t("dynamic_pricing.rollback_warning") }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="!!busyId" data-testid="dp-rollback-cancel" @click="dialog = false">{{ $t("custom.cancel") }}</v-btn>
          <v-btn color="primary" :loading="!!busyId" data-testid="dp-rollback-confirm" @click="confirm">
            {{ $t("dynamic_pricing.rollback") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { APPLIED_STATUSES, dynamicPricingApi, reportRecommendationActionError, rollbackSkippedReasons } from "@/api/dynamicPricing";
import { priceGroupLabelOptions } from "@/utils/priceGroupLabel";
import { recommendationGroupLabel, recommendationProposal } from "@/utils/dynamicPricingFormat";
import { companyNow } from "@/utils/wallClock";

// Причины пропуска строки при откате (DynamicPricing::Rollback) → ключ локали; ключи литеральные (dynamicKeys.spec.js).
const SKIP_REASON_KEYS = Object.freeze({
  changed: "dynamic_pricing.rollback_reason.changed",
  moved: "dynamic_pricing.rollback_reason.moved",
  out_of_bounds: "dynamic_pricing.rollback_reason.out_of_bounds",
  other: "dynamic_pricing.rollback_reason.other",
});

export default {
  name: "AppliedRecommendations",
  props: {
    // нормализованные рекомендации (normalizeRecommendation): applied / auto_applied — откаченные сервер в список не отдаёт
    items: { type: Array, default: () => [] },
    unit: { type: String, default: null },
  },
  data() {
    return { dialog: false, target: null, busyId: null };
  },
  methods: {
    groupLabel(rec) {
      return recommendationGroupLabel(rec, priceGroupLabelOptions(this, this.unit));
    },
    proposal(rec) {
      return recommendationProposal(rec, this.$t.bind(this));
    },
    // applied_at — момент в UTC («…Z», config.time_zone = "UTC"): дата — во времени филиала (companies.time_zone), как
    // «следующая проверка» виджета, а не в поясе браузера и не UTC-дата строки (автоприменение в 07:00 восточнее UTC+7
    // приходится на вчерашний день по UTC). Пояса нет или он неизвестен — пояс браузера.
    appliedText(rec) {
      const at = typeof rec.applied_at === "string" && rec.applied_at ? new Date(rec.applied_at) : null;
      if (!at || Number.isNaN(at.getTime())) return "";
      return this.$t("dynamic_pricing.applied_on", { date: companyNow(this.branchTimeZone(), at).format("DD.MM") });
    },
    branchTimeZone() {
      const company = this.$store && this.$store.getters && this.$store.getters.getCompany;
      return (company && company.company && company.company.time_zone) || undefined;
    },
    kindText(rec) {
      return rec.application_kind === "auto" || rec.status === "auto_applied"
        ? this.$t("dynamic_pricing.applied_auto")
        : this.$t("dynamic_pricing.applied_manual");
    },
    canRollback(rec) {
      return APPLIED_STATUSES.includes(rec.status);
    },
    ask(rec) {
      this.target = rec;
      this.dialog = true;
    },
    // «Пропущено строк цен: N. <по причинам>» — skipped_rows [{ price_id, car_id, reason }].
    skippedText(rows) {
      const n = Array.isArray(rows) ? rows.length : 0;
      if (!n) return "";
      const reasons = rollbackSkippedReasons(rows).map(([reason, count]) => this.$t(SKIP_REASON_KEYS[reason], { n: count }));
      return [this.$t("dynamic_pricing.rollback_skipped", { n }), ...reasons].join(" ");
    },
    // CTR-04: rollback → { status, skipped_rows }. changed несёт откатываемую рекомендацию: откат поправки на даты
    // отменяет и её поправки — блок настроек перечитывает их список. Строки, изменённые после применения, вышедшие за границы или машины
    // вне филиала откат пропускает (REQ-08); пропущены все — статус остаётся applied / auto_applied: ничего не откачено.
    async confirm() {
      const rec = this.target;
      if (!rec || this.busyId) return;
      this.busyId = rec.id;
      try {
        const result = (await dynamicPricingApi.rollbackRecommendation(this.axios, rec.id)) || {};
        const skipped = this.skippedText(result.skipped_rows);
        if (result.status !== "rolled_back") {
          this.$notify && this.$notify(this.$t("dynamic_pricing.rollback_nothing"), skipped, "warning", 8000);
        } else if (skipped) {
          this.$notify && this.$notify(this.$t("dynamic_pricing.rollback_partial"), skipped, "warning", 8000);
        } else {
          this.$notify && this.$notify(this.$t("dynamic_pricing.rolled_back"), "", "success");
        }
      } catch (error) {
        reportRecommendationActionError(this, error, this.$t("dynamic_pricing.rollback_error"));
      } finally {
        this.dialog = false;
        this.busyId = null;
        this.$emit("changed", rec);
      }
    },
  },
};
</script>
